import { useEffect, useRef, useState } from "react";
import { useDarkMode } from "@/hooks/useDarkMode";
import {
  useI18N,
  useRenameList,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import AnalyticsService from "@/services/analytics";
import { useAtom } from "jotai";
import { savedPlaceIdToViewDataChangedAtom } from "@/state/atoms";
import Loader from "@/components/common/Loader";
import { useCustomToast } from "@/hooks/useCustomToast";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import { XIcon } from "lucide-react";
import WideButton from "@/components/common/WideButton";
import { cn } from "@/lib/utils";
import { StandardButton } from "@/components/common/StandardButton";

interface SavedPlace {
  cover_photo: string;
  created_at: string;
  id: number;
  name: string;
  saved_place_details: object;
  total_places: number;
  updated_at: string;
  user_id: number;
  // Add any other fields that are part of your saved place object
}

interface RenameListProps {
  onClose: () => void;
  list: SavedPlace;
}

const RenameList = ({ onClose, list }: RenameListProps) => {
  const isZoomedIn = useZoomDetection();
  const isDark = useDarkMode();
  const i18n = useI18N();
  const { modalStyle, modalClassName } = useModalHeight();
  const showToast = useCustomToast();

  const [listName, setListName] = useState(list?.name);
  const { error, renameList, isLoading, result } = useRenameList();
  const [, setSavedPlaceIdToViewDataChanged] = useAtom(
    savedPlaceIdToViewDataChangedAtom,
  );

  useEffect(() => {
    if (result) {
      AnalyticsService.trackGAEventHandler("renameListListRenamed");
      AnalyticsService.trackMx("renameListListRenamed", {
        SavedPlaceName: list?.name,
        SavedPlaceId: list?.id,
        SavedPlaceTotalPlaces: list?.total_places,
      });

      showToast(i18n.get("listRenamed"), "success");

      //close the rename list modal
      onClose();

      //main list detail page reload
      setSavedPlaceIdToViewDataChanged(true);
    }
  }, [
    result,
    i18n,
    onClose,
    isDark,
    listName,
    setSavedPlaceIdToViewDataChanged,
    list?.name,
    list?.id,
    list?.total_places,
  ]);

  useEffect(() => {
    if (error) {
      console.log("error", error);

      AnalyticsService.trackGAEventHandler(
        "renameListErrorSubmittingRenameList",
      );
      AnalyticsService.trackMx("renameListErrorSubmittingRenameList", {
        SavedPlaceName: list?.name,
        SavedPlaceId: list?.id,
        SavedPlaceTotalPlaces: list?.total_places,
      });

      showToast(error.message, "error");
    }
  }, [error, isDark, list?.id, list?.name, list?.total_places]);

  const handleSave = () => {
    console.log("handleSave");

    if (!listName) {
      console.log("no listName", listName);
      return;
    }

    renameList({ name: listName, savedListId: list.id.toString() });
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <section
      style={modalStyle}
      className={cn(modalClassName)}
      role="none"
      aria-labelledby="modal-title"
    >
      {isLoading && <Loader />}

      <header
        className={`py-4 min-h-14 flex items-center justify-between text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
      >
        <StandardButton
          onClick={onClose}
          variant="closeModal"
          icon={<XIcon color={isDark ? "#A39FE8" : "#352FC3"} />}
          aria-label="Close modal"
          tabIndex={0}
          className="flex-shrink-0"
        />
        <h1
          className="title-s-semibold text-center text-fg-neutral-strong flex-1 px-4"
          ref={firstFocusRef}
          tabIndex={0}
          id="modal-title"
        >
          {i18n.get("renameList")}
        </h1>
        <div className="flex-shrink-0 w-10"></div>{" "}
        {/* Spacer to balance the layout */}
      </header>

      <div
        aria-label="Modal Content"
        role="none"
        className="flex-grow overflow-y-auto py-3 px-3"
      >
        <div className="w-full md:max-w-[70%] mx-auto mt-5">
          <div className="flex flex-col gap-4 mb-2">
            <FloatingLabelInput
              label={i18n.get("yourListName")}
              required
              type="text"
              value={listName}
              placeholder={i18n.get("enterYourListName")}
              onChange={(event) => {
                setListName(event.target.value);
              }}
              maxLength={50}
            />
          </div>

          <div className="mb-[10px] mt-[2px] flex-row">
            <div
              id="charCount"
              className="label-s-medium text-fg-neutral-subtle mt-3"
            >
              <span className={listName.length < 5 ? "text-red-600" : ""}>
                {listName.length}
              </span>
              /50 {i18n.get("characters")} - {i18n.get("atLeast5CharactersRequired")}
            </div>
          </div>
        </div>
      </div>

      <footer
        className={`py-3 min-h-14 flex items-center justify-center text-center px-6 bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky bottom-0 z-10" : ""}`}
      >
        <div className="flex flex-col justify-center items-center w-full py-6 md:py-3 md:max-w-[60%]">
          <WideButton
            className="grow"
            disabled={listName.length < 3}
            onClick={() => {
              handleSave();
            }}
          >
            {i18n.get("rename")}
          </WideButton>
        </div>
      </footer>
    </section>
  );
};

export default RenameList;
